import { FieldError } from "client/lib/apt-merchant";

import { Field } from "../../field";
import { Merchant } from "../../merchant";
import { CountryList, MerchantField } from "../../types";
import { maxLength, minLength } from "../../validate";

import {
  determineAccountApplicability,
  determineSecondBankFormEligibility,
} from "./utils";

export function validateBankAccountNumber(
  field: Field<any>,
  registeredCountry: CountryList | undefined
): void {
  if (registeredCountry === "Canada") {
    if (!minLength(7)(field.value)) {
      throw new FieldError(
        field,
        "Canadian bank account numbers must only contain numbers and be 7-12 digits long",
        "InvalidLength"
      );
    }
    if (!maxLength(12)(field.value)) {
      throw new FieldError(
        field,
        "Canadian bank account numbers must only contain numbers and be 7-12 digits long",
        "InvalidLength"
      );
    }
  }

  if (registeredCountry === "New Zealand") {
    const formatRegex = /\d{2}-\d{4}-\d{7}-\d{2,3}/;
    if (!formatRegex.test(field.value)) {
      const msg =
        "NZ bank account number must only contain numbers and match formats ##-####-#######-## or ##-####-#######-###";
      throw new FieldError(field, msg, "InvalidFormat");
    }
  }

  if (registeredCountry === "Australia") {
    const formatRegex = new RegExp("^[0-9A-Za-z]{6,10}$");
    if (!formatRegex.test(field.value)) {
      const msg =
        "AU bank account number must only contain alphanumeric characters and must be length of 6 char or more and under 10 chars";
      throw new FieldError(field, msg, "InvalidFormat");
    }
  }
}

export class AccountField extends Field<string> {
  constructor(
    model: Merchant,
    { propertyName }: { propertyName: string },
    opts: Partial<MerchantField<string>> = {}
  ) {
    super(model, {
      dataType: "string",
      propertyName,
      uiFieldType: "Input",
      uiInputBaseProps: {
        inputMode: "numeric",
      },
      ...opts,
    });
  }

  get applicable(): boolean {
    const registeredCountry = this._model.companyInformation.registeredCountry
      .value;
    const salesChannel = this._model.storeDetails.salesChannel.value;

    if (this.propertyName === "account2") {
      return determineSecondBankFormEligibility({
        salesChannel,
        registeredCountry,
      });
    }

    if (determineAccountApplicability(registeredCountry)) {
      return true;
    }
    return false;
  }

  get required(): boolean {
    if (this.propertyName === "account2") {
      const secondaryBankInfo = this._model.secondaryBank;

      return (
        secondaryBankInfo.fields
          .filter((field) => field.shown)
          .some((field) => field.value) && this.applicable
      );
    }

    return this.applicable;
  }

  get shown(): boolean {
    return this.applicable;
  }

  get testValue(): any {
    const registeredCountry = this._model.companyInformation.registeredCountry
      .value;
    if (registeredCountry === "New Zealand") {
      return "12-3456-7890123-45";
    } else if (registeredCountry === "Australia") {
      return "A1123456";
    } else {
      return "4884448888";
    }
  }

  _formatOnChange = (initialVal: string): string => {
    const registeredCountry = this._model.lead.registeredCountry.value;

    if (registeredCountry !== "New Zealand") {
      return initialVal;
    }

    // NZ Bank Account numbers follow the following formats:
    // ##-####-#######-##  OR ##-####-#######-### (where # denotes number only)
    // Formatting here to ensure that format is consistent
    const initialValLength = initialVal.length;
    if (
      initialValLength >= 15 &&
      initialVal.substring(2, 3) !== "-" &&
      initialVal.substring(6, 7) !== "-" &&
      initialVal.substring(13, 14) !== "-"
    ) {
      return (
        initialVal.substring(0, 2) +
        "-" +
        initialVal.substring(2, 6) +
        "-" +
        initialVal.substring(6, 13) +
        "-" +
        initialVal.substring(13)
      );
    }

    return initialVal;
  };

  _validate(): void {
    if (typeof this.value === "undefined") return;

    const registeredCountry = this._model.companyInformation.registeredCountry
      .value;

    validateBankAccountNumber(this, registeredCountry);
  }
}
